import '../../waterline-management/WaterlineManagement.css'
import { useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { Box, Card, Link, useTheme } from '@mui/material'
import { selectCustomerNumber, selectGroups } from '../../../../redux/customer'
import { useAppDispatch, useAppSelector } from '../../../../redux/store'
import { selectCurrentUser } from '../../../../redux/user'
import { Groups, TestCompliance } from '../../../../types'
import CorpCalendarTable from '../../../../components/CorpCalendarTable'
import { getCorpBioTestResults } from '../services'
import { BioCalendarRow, BioCustomer } from '../types'
import { getCalendarData, getFilteredPractices } from '../utils/helpers'
import CorpCalendarFilters from '../../../../components/CorpCalendarFilters'
import BiologicalExportButton from './BiologicalExportButton'
import dayjs, { Dayjs } from 'dayjs'
import isoWeek from "dayjs/plugin/isoWeek"
import BiologicalCalendarResultsCell from './BiologicalCalendarResultsCell'
import MonthHeader from './MonthHeader'
import { FilterOption, WaterlineCalendarRow } from '../../waterline-management/types'
import DateRangeSelector from './DateRangeSelector'
import { useNavigate } from 'react-router-dom'
import { addError } from '../../../../redux/alerts'

dayjs.extend(isoWeek)

const filterOptions: FilterOption[] = [
  { label: 'All Results', value: 'All results' },
  { label: 'Invalid Tests', value: TestCompliance.invalidTest },
  { label: 'Failed Tests', value: TestCompliance.failedTest },
  { label: 'Passed Tests', value: TestCompliance.passedTest },
  { label: 'Missed Tests', value: TestCompliance.missedTest },
]

const BiologicalCalendar = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate()
  const initialFromDate = dayjs().subtract(12, 'weeks').startOf('isoWeek')
  const initialToDate = dayjs()

  const [search, setSearch] = useState('')
  const [filterByType, setFilterByType] = useState('')
  const [filterByGroup, setFilterByGroup] = useState<Groups[]>([])
  const [practices, setPractices] = useState<BioCalendarRow[] | null>(null)
  const [bioCustomer, setBioCustomer] = useState<BioCustomer | null>(null)
  const [fromDate, setFromDate] = useState<Dayjs>(initialFromDate)
  const [toDate, setToDate] = useState<Dayjs>(initialToDate)
  const [isLoading, setIsLoading] = useState(true)

  const customerNumber = useSelector(selectCustomerNumber)
  const currentUser = useAppSelector(selectCurrentUser)
  const groups = useSelector(selectGroups)

  const fetchBioTestResults = async () => {
    try {
      if (currentUser && customerNumber) {
        setIsLoading(true)

        const bioCustomer: BioCustomer = await getCorpBioTestResults(currentUser, customerNumber, fromDate, toDate)
        setBioCustomer(bioCustomer)
      }
    } catch (error: any) {
      setIsLoading(false)
      console.error(error)
      dispatch(addError(error instanceof Error ? error.message : 'An error has occurred while processing your request.'))
    }
  }

  useEffect(() => {
    fetchBioTestResults()
  }, [customerNumber, currentUser, fromDate, toDate])

  useEffect(() => {
    if (bioCustomer?.children?.length) {
      const data = getCalendarData(bioCustomer)
      setPractices(data)
      setIsLoading(false)
    }
  }, [bioCustomer])

  const filteredPractices = useMemo(() => {
    if (search || filterByType || filterByGroup?.length) {
      const childPractices = structuredClone(practices) ?? []
      const filtered = getFilteredPractices(search, filterByType, filterByGroup, childPractices)
  
      return filtered
    } else {
      return practices ?? []
    }
  }, [practices, filterByType, filterByGroup, search])

  const setDateRange = (from: Dayjs, to: Dayjs) => {
    setFromDate(from)
    setToDate(to)
  }

  const PracticeLink = ({ practice }: { practice: BioCalendarRow | WaterlineCalendarRow }) => {
    const theme = useTheme()
    return (
      <Link
        component="button"
        onClick={() => navigate(`/corp/practice/${practice.customerNumber}/biological`)}
        sx={{ color: theme.palette.linkColor.main, textDecoration: 'none' }}
      >
        {practice.practiceName}
      </Link>
    )
  }

  return (
    <Card sx={{ width: "100%" }} variant="outlined">
      <Box className="box-header">
        {filteredPractices.length} Practice{filteredPractices.length === 1 ? '' : 's'}
      </Box>
      <Box sx={{ padding: '12px 16px' }}>
        <CorpCalendarFilters
          groups={groups}
          filterByType={filterByType}
          filterByGroup={filterByGroup}
          setFilterByType={setFilterByType}
          setFilterByGroup={setFilterByGroup}
          setSearch={setSearch}
          filterOptions={filterOptions}
        />
        <DateRangeSelector
          isLoading={isLoading}
          setDateRange={setDateRange}
        />
      </Box>
      <Box className="table-subheader flex-space-between">
        <Box className="flex">
          <div className="background-danger test-label bold">Failure</div>
          <div className="background-warning test-label bold">Missed/Retest</div>
          <div className="background-success test-label bold">Passed</div>
          <div className="background-inactive test-label bold">Not Started</div>
        </Box>
        <BiologicalExportButton
          calendarData={filteredPractices}
          customerNumber={customerNumber}
          fromDate={fromDate}
          toDate={toDate}
          groupIds={filterByGroup.map(group => group.group_id)}
        />
      </Box>
      <Box className="waterline-table-wrapper">
        <CorpCalendarTable
          practices={filteredPractices}
          isLoading={isLoading}
          testResultsCell={BiologicalCalendarResultsCell}
          monthHeader={
            <MonthHeader
              fromDate={fromDate.format('YYYY-MM-DD')}
              toDate={toDate.format('YYYY-MM-DD')}
            />
          }
          practiceLink={PracticeLink}
        />
      </Box>
    </Card>
  )
}

export default BiologicalCalendar
